import { LoadingState } from "@/ds/custom/LoadingState";
import { Modal, ModalBody, ModalDescription, ModalHeader, ModalTitle } from "@/ds/custom/Modal";
import { Button } from "@/ds/shadcn/components/ui/button";
import { Input } from "@/ds/shadcn/components/ui/input";
import { useShareableLink } from "@/features/collections/api/useShareableLink";
import { useSubscription } from "@/features/common/api/useSubscription";
import { SubscriptionGate } from "@/features/common/components/SubscriptionGate";
import { ErrorDisplay } from "@/lib/common/ErrorDisplay";
import { CopyIcon } from "lucide-react";
import { useCallback, type FC } from "react";
import { toast } from "sonner";

export interface ModalShareableLinkProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	collectionId: string;
}

export const ModalShareableLink: FC<ModalShareableLinkProps> = props => {
	const {
		open,
		onOpenChange,
		collectionId
	} = props;

	const subscription = useSubscription();
	const isPremium = !!subscription.data?.activeSubscription;

	return (
		<Modal open={open} onOpenChange={onOpenChange}>
			<ModalHeader>
				<ModalTitle>Share Collection</ModalTitle>
				{
					isPremium &&
					<ModalDescription>
						The link below can be used to view the contents of this collection publicly. Only market values and quantities are displayed — no statistics around investments, gains or profits are ever public.
					</ModalDescription>
				}
			</ModalHeader>
			<ModalBody>
				<SubscriptionGate featureName="Collection Sharing" compact>
					<ModalShareableLinkContent collectionId={collectionId} open={open} />
				</SubscriptionGate>
			</ModalBody>
		</Modal>
	);
};

interface ModalShareableLinkContentProps {
	collectionId: string;
	open: boolean;
}

const ModalShareableLinkContent: FC<ModalShareableLinkContentProps> = props => {
	const {
		collectionId,
		open
	} = props;

	const shareableLink = useShareableLink(collectionId, open);

	const onCopy = useCallback(() => {
		if (!shareableLink.data) {
			return;
		}

		navigator.clipboard.writeText(shareableLink.data.shareableLink);
		toast.success("Copied to clipboard", { position: "bottom-center" });
	}, [shareableLink.data]);

	return (
		<div className="flex flex-col gap-4">
			{shareableLink.isLoading && <LoadingState />}

			<ErrorDisplay error={shareableLink.error} />

			{
				shareableLink.data &&
				<div className="flex items-center gap-2">
					<Input readOnly value={shareableLink.data.shareableLink} onFocus={e => e.target.select()} />
					<Button variant="outline" size="icon" onClick={onCopy}>
						<CopyIcon />
					</Button>
				</div>
			}
		</div>
	);
};
